import { z } from "zod";
import { bdPhone, paymentRequestMethodSchema, platformInvoiceSchema, trxIdSchema } from "./billing.js";
import { defineEndpoint } from "./endpoint.js";
import { ADDON_KEYS } from "./entitlements.js";
import { paisa } from "./money.js";
/**
 * PL-13 (docs/specs/plan-catalogue.md "Add-on prices"): add-ons the business buys through billing — extra locations
 * and POS devices until the current period ends, SMS packs that never expire. The Super Admin sets the prices.
 */
const addonKeySchema = z.enum(ADDON_KEYS);
const reason = z.string().trim().min(1).max(500);
/** SMS packs are one-off credits; extra locations and devices run with the subscription period. */
export const RECURRING_ADDONS = ["locations.max", "devices.max"];
// Super Admin --------------------------------------------------------------------------------------------------
export const addonPriceSchema = z.object({
    key: addonKeySchema,
    /** How much one pack adds: 1 location, 1 device, 1,000 SMS credits. */
    unit: z.number().int(),
    /** Per pack: a month of a recurring add-on (yearly cycles pay 12 ×), or one SMS pack. */
    pricePaisa: paisa(),
    recurring: z.boolean(),
    /** Off: businesses cannot buy it. */
    active: z.boolean(),
    version: z.number().int(),
});
export const addonPriceListSchema = z.object({ data: z.array(addonPriceSchema) });
export const listAddonPrices = defineEndpoint({
    tag: "platform-admin",
    name: "listAddonPrices",
    method: "GET",
    path: "/v1/platform/addon-prices",
    access: { platformAdmin: true },
    summary: "Add-on prices: extra locations, POS devices and SMS packs",
    status: 200,
    response: addonPriceListSchema,
});
export const setAddonPriceSchema = z.object({
    unit: z.number().int().min(1).max(1_000_000),
    pricePaisa: paisa().positive(),
    active: z.boolean(),
    version: z.number().int().min(1),
    reason,
});
export const setAddonPrice = defineEndpoint({
    tag: "platform-admin",
    name: "setAddonPrice",
    method: "PUT",
    path: "/v1/platform/addon-prices/{key}",
    access: { platformAdmin: true },
    summary: "Change an add-on's pack size, price or availability (new invoices only)",
    params: z.object({ key: addonKeySchema }),
    body: setAddonPriceSchema,
    status: 200,
    errors: ["CONFLICT"],
    response: addonPriceListSchema,
});
// Business -----------------------------------------------------------------------------------------------------
export const billingAddonsSchema = z.object({
    /** Add-ons on sale. */
    offers: z.array(addonPriceSchema.omit({ active: true, version: true })),
    /** Add-ons in force now (bought or granted). */
    active: z.array(z.object({
        key: addonKeySchema,
        quantity: z.number().int(),
        /** null: until cancelled, or an SMS pack (never expires). */
        endsAt: z.iso.datetime().nullable(),
        source: z.enum(["manual", "billing"]),
    })),
    /** Buying needs a paid subscription (the add-on runs with its period). */
    canBuy: z.boolean(),
});
export const listBillingAddons = defineEndpoint({
    tag: "billing",
    name: "listBillingAddons",
    method: "GET",
    path: "/v1/billing/addons",
    access: { permission: "billing.view" },
    summary: "Add-ons on sale and the ones in force",
    status: 200,
    response: billingAddonsSchema,
});
export const addonQuoteRequestSchema = z.object({
    key: addonKeySchema,
    packs: z.number().int().min(1).max(100),
});
export const addonQuoteSchema = z.object({
    key: addonKeySchema,
    packs: z.number().int(),
    /** What the business gets: packs × unit. */
    quantity: z.number().int(),
    subtotalPaisa: paisa(),
    vatBp: z.number().int(),
    vatPaisa: paisa(),
    totalPaisa: paisa(),
    /** When a recurring add-on ends (the period end); null for an SMS pack. */
    endsAt: z.iso.datetime().nullable(),
});
export const quoteBillingAddon = defineEndpoint({
    tag: "billing",
    name: "quoteBillingAddon",
    method: "POST",
    path: "/v1/billing/addons/quote",
    access: { permission: "billing.manage" },
    summary: "What an add-on costs now (the rest of the period for extra locations / devices)",
    body: addonQuoteRequestSchema,
    status: 200,
    errors: ["BUSINESS_RULE_VIOLATION"],
    response: addonQuoteSchema,
});
export const buyAddonRequestSchema = addonQuoteRequestSchema
    .extend({
    method: paymentRequestMethodSchema,
    senderPhone: bdPhone.optional(),
    trxId: trxIdSchema.optional(),
    note: z.string().trim().max(500).default(""),
})
    .superRefine((body, ctx) => {
    if (body.method !== "bkash_manual")
        return;
    if (body.senderPhone === undefined)
        ctx.addIssue({ code: "custom", path: ["senderPhone"], message: "Required" });
    if (body.trxId === undefined)
        ctx.addIssue({ code: "custom", path: ["trxId"], message: "Required" });
});
export const requestAddonPayment = defineEndpoint({
    tag: "billing",
    name: "requestAddonPayment",
    method: "POST",
    path: "/v1/billing/addons/payment-requests",
    access: { permission: "billing.manage" },
    summary: "Issue the add-on's invoice and ask for approval of a bKash or cash payment",
    body: buyAddonRequestSchema,
    idempotent: true,
    status: 201,
    errors: ["TRX_ID_USED", "REQUEST_PENDING", "BUSINESS_RULE_VIOLATION"],
    response: platformInvoiceSchema,
});
//# sourceMappingURL=billing-addons.js.map